'use client'

import * as React from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import { useTranslations } from 'next-intl'
import { AppearanceControls } from './appearance-controls'
import type { BracketAppearance } from './appearance'
import {
  ChevronDown,
  Eraser,
  Eye,
  EyeOff,
  ImageOff,
  ImagePlus,
  Palette,
  Settings,
  Shuffle,
  Sparkles,
  Upload,
  Users,
  X,
} from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'

import {
  BRACKET_BACKGROUND_FIT_OPTIONS,
  BRACKET_BACKGROUND_POSITION_OPTIONS,
  BRACKET_BACKGROUND_PRESETS,
  QUICK_COUNTS,
} from './constants'
import type {
  BracketBackgroundPresetId,
  BracketBackgroundSettings,
  BracketDirection,
  BracketDisplayMode,
  BracketMakerCopy,
  BracketStructure,
  BracketTheme,
  DisplayModeOption,
  DirectionOption,
  StructureOption,
} from './types'

type BracketControlsPanelProps = {
  appearance: BracketAppearance
  onAppearanceChange: (patch: Partial<BracketAppearance>) => void
  appearanceStorageError: boolean
  onShuffle: () => void
  shuffleCount: number
  backgroundSettings: BracketBackgroundSettings
  copy: BracketMakerCopy
  direction: BracketDirection
  displayMode: BracketDisplayMode
  displayModeOptions: DisplayModeOption[]
  directionOptions: DirectionOption[]
  onBackgroundPresetChange: (value: BracketBackgroundPresetId) => void
  onBackgroundRemove: () => void
  onBackgroundSettingChange: (value: Partial<BracketBackgroundSettings>) => void
  onBackgroundUpload: (file: File) => void
  onClearAll: () => void
  onDirectionChange: (value: BracketDirection) => void
  onDisplayModeChange: (value: BracketDisplayMode) => void
  onFillSample: () => void
  onNamesChange: (value: string) => void
  onOpenImageDialog: () => void
  onTeamCountChange: (value: number) => void
  onToggleNames: () => void
  participants: string[]
  seededPlacement: boolean
  showNames: boolean
  structure: BracketStructure
  structureOptions: StructureOption[]
  teamCount: number
  theme: BracketTheme
  toggleNamesLabel: string
  onStructureChange: (value: BracketStructure) => void
}

type ActivePanel = 'teams' | 'appearance' | null

function TeamNamesInput({
  participants,
  onNamesChange,
  placeholder,
  className,
}: {
  participants: string[]
  onNamesChange: (value: string) => void
  placeholder: string
  className: string
}) {
  const [value, setValue] = React.useState(() => participants.join('\n'))
  const isFocused = React.useRef(false)
  React.useEffect(() => {
    // 编辑时保留原始换行和光标位置；外部重置或切换队伍数时再同步名单。
    if (!isFocused.current) setValue(participants.join('\n'))
  }, [participants])
  return (
    <Textarea
      value={value}
      onFocus={() => {
        isFocused.current = true
      }}
      onBlur={() => {
        isFocused.current = false
      }}
      onChange={(event) => {
        const next = event.target.value
        setValue(next)
        onNamesChange(next)
      }}
      placeholder={placeholder}
      rows={18}
      className={className}
    />
  )
}

type RangeControlProps = {
  label: string
  value: number
  min: number
  max: number
  step: number
  onChange: (value: number) => void
  formatValue: (value: number) => string
}

type EditorDrawerProps = {
  children: React.ReactNode
  description?: string
  onClose: () => void
  title: string
  closeLabel: string
}

type ControlSectionProps = {
  children: React.ReactNode
  className?: string
  icon: React.ComponentType<{ className?: string }>
  label: string
}

type SegmentButtonProps = {
  children?: React.ReactNode
  className?: string
  icon?: React.ComponentType<{ className?: string }>
  label: string
  onClick: () => void
  selected?: boolean
  title?: string
}

function RangeControl({
  label,
  value,
  min,
  max,
  step,
  onChange,
  formatValue,
}: RangeControlProps) {
  return (
    <label className="grid w-full gap-1.5">
      <span className="flex items-center justify-between gap-3 text-xs font-medium text-muted-foreground">
        <span>{label}</span>
        <span className="tabular-nums text-foreground/80">
          {formatValue(value)}
        </span>
      </span>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(event) => onChange(Number(event.currentTarget.value))}
        className="h-2 w-full cursor-pointer appearance-none rounded-full bg-muted accent-primary"
      />
    </label>
  )
}

function EditorDrawer({
  children,
  description,
  onClose,
  title,
  closeLabel,
}: EditorDrawerProps) {
  const previousFocus = React.useRef<HTMLElement | null>(
    typeof document === 'undefined'
      ? null
      : (document.activeElement as HTMLElement),
  )
  return (
    <Dialog.Root
      open
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[110] bg-background/35 backdrop-blur-[2px] print:hidden" />
        <Dialog.Content
          {...(!description ? { 'aria-describedby': undefined } : {})}
          onCloseAutoFocus={(event) => {
            event.preventDefault()
            previousFocus.current?.focus()
          }}
          className="fixed right-0 top-0 z-[111] flex h-dvh w-full max-w-[440px] flex-col border-l bg-background text-foreground shadow-2xl outline-none print:hidden"
        >
          <div className="flex items-start justify-between gap-4 border-b px-5 py-4">
            <div className="min-w-0 space-y-1">
              <Dialog.Title className="text-base font-semibold leading-tight">
                {title}
              </Dialog.Title>
              {description && (
                <Dialog.Description className="text-xs leading-relaxed text-muted-foreground">
                  {description}
                </Dialog.Description>
              )}
            </div>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8 shrink-0"
              onClick={onClose}
              aria-label={closeLabel}
              title={closeLabel}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5">
            {children}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

function ControlSection({
  children,
  className,
  icon: Icon,
  label,
}: ControlSectionProps) {
  return (
    <section
      className={cn(
        'rounded-lg border bg-background p-3 shadow-[0_1px_0_rgba(15,23,42,0.03)]',
        className,
      )}
    >
      <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-muted-foreground">
        <Icon className="h-4 w-4" />
        {label}
      </div>
      <div
        className="grid overflow-hidden rounded-md border bg-background"
        role="group"
        aria-label={label}
      >
        {children}
      </div>
    </section>
  )
}

function SegmentButton({
  children,
  className,
  icon: Icon,
  label,
  onClick,
  selected,
  title,
}: SegmentButtonProps) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      title={title}
      className={cn(
        'inline-flex min-h-11 cursor-pointer items-center justify-center gap-2 border-r px-3 text-sm font-semibold transition last:border-r-0 focus-visible:z-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30',
        selected
          ? 'bg-primary text-primary-foreground shadow-sm'
          : 'text-muted-foreground hover:bg-muted hover:text-foreground',
        className,
      )}
      onClick={onClick}
    >
      {Icon ? <Icon className="h-4 w-4 shrink-0" /> : null}
      <span className="truncate">{label}</span>
      {children}
    </button>
  )
}

const percent = (value: number) => `${Math.round(value * 100)}%`
const pixels = (value: number) => `${value}px`

export function BracketControlsPanel({
  appearance,
  onAppearanceChange,
  appearanceStorageError,
  onShuffle,
  shuffleCount,
  backgroundSettings,
  copy,
  direction,
  displayMode,
  displayModeOptions,
  directionOptions,
  onBackgroundPresetChange,
  onBackgroundRemove,
  onBackgroundSettingChange,
  onBackgroundUpload,
  onClearAll,
  onDirectionChange,
  onDisplayModeChange,
  onFillSample,
  onNamesChange,
  onOpenImageDialog,
  onStructureChange,
  onTeamCountChange,
  onToggleNames,
  participants,
  seededPlacement,
  showNames,
  structure,
  structureOptions,
  teamCount,
  theme,
  toggleNamesLabel,
}: BracketControlsPanelProps) {
  const t = useTranslations('maker')
  const [activePanel, setActivePanel] = React.useState<ActivePanel>(null)

  React.useEffect(() => {
    if (!activePanel) return

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setActivePanel(null)
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [activePanel])

  React.useEffect(() => {
    if (!activePanel) return

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    return () => {
      document.body.style.overflow = previousOverflow
    }
  }, [activePanel])

  const backgroundChoices: Array<{
    id: BracketBackgroundPresetId
    image: string | null
  }> = [
    { id: 'none', image: null },
    ...BRACKET_BACKGROUND_PRESETS.map((preset) => ({
      id: preset.id,
      image: preset.image,
    })),
    { id: 'custom', image: backgroundSettings.customImage },
  ]

  const activeBackgroundImage =
    backgroundSettings.preset === 'custom'
      ? backgroundSettings.customImage
      : BRACKET_BACKGROUND_PRESETS.find(
          (preset) => preset.id === backgroundSettings.preset,
        )?.image

  return (
    <div className="relative print:hidden">
      <div className="overflow-hidden rounded-lg border bg-background shadow-sm">
        <div className="flex flex-wrap items-center gap-4 px-4 py-4 sm:px-6">
          <div className="flex min-w-0 flex-wrap items-center gap-3">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-10 gap-3 rounded-md px-2 text-base font-semibold hover:bg-muted"
              aria-label={`${copy.editor.editTeams}: ${copy.editor.teamCountSummary}`}
              onClick={() => setActivePanel('teams')}
            >
              <Users className="h-6 w-6" />
              <span>{copy.editor.editTeams}</span>
              <span className="rounded-full border bg-background px-3 py-1 text-sm font-semibold text-muted-foreground">
                {copy.editor.teamCountSummary}
              </span>
            </Button>

            <Button
              type="button"
              variant="outline"
              size="sm"
              className="gap-2"
              onClick={onShuffle}
              disabled={participants.filter((name) => name.trim()).length < 2}
            >
              <Shuffle className="h-4 w-4" />
              {t('shuffle')}
            </Button>
            <span className="sr-only" role="status">
              {shuffleCount > 0
                ? t('shuffleDone', { count: shuffleCount })
                : ''}
            </span>

            <div className="hidden h-9 w-px bg-border sm:block" />

            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-10 gap-3 rounded-md px-2 text-base font-semibold hover:bg-muted"
              aria-label={`${copy.editor.appearance}: ${copy.background.presets[backgroundSettings.preset]}`}
              onClick={() => setActivePanel('appearance')}
            >
              <span
                aria-hidden="true"
                className="h-7 w-7 shrink-0 overflow-hidden rounded-sm border bg-muted"
              >
                {activeBackgroundImage ? (
                  <span
                    className="block h-full w-full bg-cover bg-center"
                    style={{
                      backgroundImage: `url(${JSON.stringify(
                        activeBackgroundImage,
                      )})`,
                    }}
                  />
                ) : null}
              </span>
              <Palette className="h-6 w-6" />
              <span>{copy.editor.appearance}</span>
              <span className="inline-flex h-9 max-w-[130px] items-center gap-2 rounded-md border bg-background px-3 text-sm font-medium text-muted-foreground">
                <span className="truncate">
                  {copy.background.presets[backgroundSettings.preset]}
                </span>
                <ChevronDown className="h-4 w-4 shrink-0" />
              </span>
            </Button>
          </div>
        </div>

        <div className="border-t bg-muted/20 px-4 py-4 sm:px-6">
          <div className="grid gap-4 lg:grid-cols-4">
            <ControlSection icon={Settings} label={copy.editor.setup}>
              <div className="grid grid-cols-2">
                {structureOptions.map((option) => (
                  <SegmentButton
                    key={option.value}
                    icon={option.icon}
                    label={option.label}
                    selected={structure === option.value}
                    title={option.hint}
                    onClick={() => onStructureChange(option.value)}
                  />
                ))}
              </div>
            </ControlSection>

            <ControlSection
              icon={
                directionOptions.find((option) => option.value === direction)
                  ?.icon ?? Settings
              }
              label={copy.directionLabel}
            >
              <div className="grid grid-cols-2">
                {directionOptions.map((option) => (
                  <SegmentButton
                    key={option.value}
                    icon={option.icon}
                    label={option.label}
                    selected={direction === option.value}
                    title={option.hint}
                    onClick={() => onDirectionChange(option.value)}
                  />
                ))}
              </div>
            </ControlSection>

            <ControlSection
              icon={
                displayModeOptions.find(
                  (option) => option.value === displayMode,
                )?.icon ?? Settings
              }
              label={copy.displayModeLabel}
            >
              <div className="grid grid-cols-2">
                {displayModeOptions.map((option) => (
                  <SegmentButton
                    key={option.value}
                    icon={option.icon}
                    label={option.label}
                    selected={displayMode === option.value}
                    title={option.hint}
                    onClick={() => onDisplayModeChange(option.value)}
                  />
                ))}
              </div>
            </ControlSection>

            <ControlSection icon={Users} label={copy.teams}>
              <div className="grid grid-cols-5">
                {QUICK_COUNTS.map((count) => (
                  <SegmentButton
                    key={count}
                    label={String(count)}
                    selected={teamCount === count}
                    onClick={() => onTeamCountChange(count)}
                  />
                ))}
              </div>
            </ControlSection>
          </div>
        </div>
      </div>

      {activePanel === 'teams' ? (
        <EditorDrawer
          title={copy.participants}
          description={copy.namesPlaceholder}
          closeLabel={copy.editor.closePanel}
          onClose={() => setActivePanel(null)}
        >
          <div className="space-y-6">
            <section className="space-y-3">
              <div className="flex items-center justify-between gap-3">
                <h3 className={theme.controlTitle}>{copy.teams}</h3>
                <span className="rounded-full bg-muted px-2.5 py-1 text-xs font-medium text-muted-foreground">
                  {copy.editor.teamCountSummary}
                </span>
              </div>
              <div className="grid grid-cols-5 gap-2">
                {QUICK_COUNTS.map((count) => (
                  <Button
                    key={count}
                    type="button"
                    variant={teamCount === count ? 'secondary' : 'outline'}
                    size="sm"
                    className={cn(
                      'h-9 px-2.5',
                      teamCount === count &&
                        'bg-primary text-primary-foreground hover:bg-primary/90',
                    )}
                    onClick={() => onTeamCountChange(count)}
                  >
                    {count}
                  </Button>
                ))}
              </div>
            </section>

            <section className="space-y-3">
              <div className="flex w-full items-center justify-between gap-3">
                <h3 className={theme.controlTitle}>{copy.participants}</h3>
                <div className="flex gap-1">
                  {displayMode === 'image' ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      aria-label={toggleNamesLabel}
                      title={toggleNamesLabel}
                      onClick={onToggleNames}
                    >
                      {showNames ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </Button>
                  ) : null}
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    aria-label={copy.clearList}
                    title={copy.clearList}
                    onClick={onClearAll}
                  >
                    <Eraser className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    aria-label={copy.fillSample}
                    title={copy.fillSample}
                    onClick={onFillSample}
                  >
                    <Sparkles className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              {seededPlacement && (
                <p className="text-sm text-muted-foreground">
                  {t('seededNamesHint', { count: teamCount })}
                </p>
              )}
              <p className="text-xs text-muted-foreground">
                {t('shuffleHint')}
              </p>
              <TeamNamesInput
                participants={participants}
                onNamesChange={onNamesChange}
                placeholder={copy.namesPlaceholder}
                className={cn(theme.nameInput, 'min-h-[360px]')}
              />
            </section>

            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-10 w-full justify-center text-sm"
              onClick={onOpenImageDialog}
            >
              <ImagePlus className="mr-2 h-4 w-4" />
              {copy.bulkUpload}
            </Button>
          </div>
        </EditorDrawer>
      ) : null}

      {activePanel === 'appearance' ? (
        <EditorDrawer
          title={copy.editor.appearance}
          closeLabel={copy.editor.closePanel}
          onClose={() => setActivePanel(null)}
        >
          <div className="space-y-6">
            <AppearanceControls
              appearance={appearance}
              onChange={onAppearanceChange}
              storageError={appearanceStorageError}
            />
            <details className="rounded-md border p-3">
              <summary className="cursor-pointer text-sm font-semibold">
                {copy.background.title}
              </summary>
              <div className="mt-4 space-y-6">
                <section className="space-y-3">
                  <div className="flex items-center justify-between gap-3">
                    <h3 className={theme.controlTitle}>
                      {copy.background.title}
                    </h3>
                    <span className="inline-flex h-6 items-center gap-1 rounded-full bg-muted px-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                      <Palette className="h-3 w-3" />
                      {copy.background.presets[backgroundSettings.preset]}
                    </span>
                  </div>

                  <div className="grid w-full grid-cols-2 gap-2">
                    {backgroundChoices.map((choice) => {
                      const selected = backgroundSettings.preset === choice.id

                      return (
                        <button
                          key={choice.id}
                          type="button"
                          aria-pressed={selected}
                          className={cn(
                            'relative h-20 cursor-pointer overflow-hidden rounded-md border text-left text-xs font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30',
                            selected
                              ? 'border-primary text-primary shadow-sm ring-1 ring-primary/30'
                              : 'border-border text-foreground/80 hover:border-primary/50',
                          )}
                          onClick={() => onBackgroundPresetChange(choice.id)}
                        >
                          {choice.image ? (
                            <span
                              aria-hidden="true"
                              className="absolute inset-0 bg-cover bg-center"
                              style={{
                                backgroundImage: `url(${JSON.stringify(
                                  choice.image,
                                )})`,
                              }}
                            />
                          ) : (
                            <span
                              aria-hidden="true"
                              className="absolute inset-0 bg-[linear-gradient(135deg,hsl(var(--muted))_25%,transparent_25%,transparent_50%,hsl(var(--muted))_50%,hsl(var(--muted))_75%,transparent_75%)] bg-[length:16px_16px]"
                            />
                          )}
                          <span className="absolute inset-0 bg-gradient-to-t from-background via-background/45 to-transparent" />
                          {choice.id === 'custom' && !choice.image ? (
                            <span className="absolute inset-0 flex items-center justify-center text-muted-foreground">
                              <ImagePlus className="h-4 w-4" />
                            </span>
                          ) : null}
                          <span className="absolute bottom-2 left-2 right-2 truncate">
                            {copy.background.presets[choice.id]}
                          </span>
                        </button>
                      )
                    })}
                  </div>

                  <div className="flex w-full items-center gap-2">
                    <label className="inline-flex h-10 flex-1 cursor-pointer items-center justify-center rounded-md border border-input bg-background px-3 text-sm font-medium text-muted-foreground transition hover:text-foreground">
                      <Upload className="mr-2 h-4 w-4" />
                      {copy.background.upload}
                      <input
                        type="file"
                        accept="image/*"
                        className="sr-only"
                        onChange={(event) => {
                          const file = event.currentTarget.files?.[0]
                          if (file) onBackgroundUpload(file)
                          event.currentTarget.value = ''
                        }}
                      />
                    </label>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-10 px-3 text-sm"
                      disabled={!backgroundSettings.customImage}
                      onClick={onBackgroundRemove}
                    >
                      <ImageOff className="mr-2 h-4 w-4" />
                      {copy.background.remove}
                    </Button>
                  </div>
                </section>

                <section className="grid w-full gap-3 rounded-md border border-border/70 p-3">
                  <RangeControl
                    label={copy.background.opacity}
                    min={0}
                    max={1}
                    step={0.01}
                    value={backgroundSettings.opacity}
                    formatValue={percent}
                    onChange={(opacity) =>
                      onBackgroundSettingChange({ opacity })
                    }
                  />
                  <RangeControl
                    label={copy.background.overlay}
                    min={0}
                    max={0.85}
                    step={0.01}
                    value={backgroundSettings.overlayOpacity}
                    formatValue={percent}
                    onChange={(overlayOpacity) =>
                      onBackgroundSettingChange({ overlayOpacity })
                    }
                  />
                  <RangeControl
                    label={copy.background.blur}
                    min={0}
                    max={8}
                    step={1}
                    value={backgroundSettings.blur}
                    formatValue={pixels}
                    onChange={(blur) => onBackgroundSettingChange({ blur })}
                  />
                </section>

                <section className="grid w-full grid-cols-2 gap-3">
                  <div className="grid gap-1.5">
                    <span className="text-xs font-medium text-muted-foreground">
                      {copy.background.fitLabel}
                    </span>
                    <div className="grid grid-cols-3 gap-1">
                      {BRACKET_BACKGROUND_FIT_OPTIONS.map((fit) => (
                        <Button
                          key={fit}
                          type="button"
                          variant={
                            backgroundSettings.fit === fit
                              ? 'secondary'
                              : 'ghost'
                          }
                          size="sm"
                          className={cn(
                            'h-8 px-2 text-[11px]',
                            backgroundSettings.fit === fit &&
                              'bg-primary text-primary-foreground hover:bg-primary/90',
                          )}
                          onClick={() => onBackgroundSettingChange({ fit })}
                        >
                          {copy.background.fits[fit]}
                        </Button>
                      ))}
                    </div>
                  </div>
                  <div className="grid gap-1.5">
                    <span className="text-xs font-medium text-muted-foreground">
                      {copy.background.positionLabel}
                    </span>
                    <div className="grid grid-cols-3 gap-1">
                      {BRACKET_BACKGROUND_POSITION_OPTIONS.map((position) => (
                        <Button
                          key={position}
                          type="button"
                          variant={
                            backgroundSettings.position === position
                              ? 'secondary'
                              : 'ghost'
                          }
                          size="sm"
                          className={cn(
                            'h-8 px-2 text-[11px]',
                            backgroundSettings.position === position &&
                              'bg-primary text-primary-foreground hover:bg-primary/90',
                          )}
                          onClick={() =>
                            onBackgroundSettingChange({ position })
                          }
                        >
                          {copy.background.positions[position]}
                        </Button>
                      ))}
                    </div>
                  </div>
                </section>

                <section className="grid w-full grid-cols-2 gap-2">
                  <Button
                    type="button"
                    variant={
                      backgroundSettings.overlayTone === 'light'
                        ? 'secondary'
                        : 'ghost'
                    }
                    size="sm"
                    className={cn(
                      'h-9 px-3 text-xs',
                      backgroundSettings.overlayTone === 'light' &&
                        'bg-primary text-primary-foreground hover:bg-primary/90',
                    )}
                    onClick={() =>
                      onBackgroundSettingChange({ overlayTone: 'light' })
                    }
                  >
                    {copy.background.toneLight}
                  </Button>
                  <Button
                    type="button"
                    variant={
                      backgroundSettings.overlayTone === 'dark'
                        ? 'secondary'
                        : 'ghost'
                    }
                    size="sm"
                    className={cn(
                      'h-9 px-3 text-xs',
                      backgroundSettings.overlayTone === 'dark' &&
                        'bg-primary text-primary-foreground hover:bg-primary/90',
                    )}
                    onClick={() =>
                      onBackgroundSettingChange({ overlayTone: 'dark' })
                    }
                  >
                    {copy.background.toneDark}
                  </Button>
                </section>

                <label className="flex w-full cursor-pointer items-center gap-2 rounded-md border border-border/70 px-3 py-2 text-xs text-muted-foreground">
                  <Checkbox
                    checked={backgroundSettings.print}
                    onCheckedChange={(checked) =>
                      onBackgroundSettingChange({ print: checked === true })
                    }
                  />
                  <span>{copy.background.print}</span>
                </label>
              </div>
            </details>
          </div>
        </EditorDrawer>
      ) : null}
    </div>
  )
}
